import React from "react";
import { motion } from "framer-motion";
import {
  FaFileContract,
  FaUserCheck,
  FaExclamationTriangle,
  FaHandshake,
  FaGavel,
} from "react-icons/fa";
import { CLINIC_GOOGLE_MAPS_LINK } from "../../utils/addressUtils";
import {
  HELPDESK_PHONE_DISPLAY,
  EMERGENCY_PHONE_DISPLAY,
  HELPDESK_PHONE_TEL,
  EMERGENCY_PHONE_TEL,
} from "../../utils/clinicContact";
import "./Terms.css";

const Terms = () => {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="terms-page">
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="container">
          <motion.div
            className="terms-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Terms of Service</h1>
            <p>Understanding our service terms and conditions</p>
            <div className="terms-badge">
              <FaFileContract className="terms-icon" />
              <span>Last Updated: {currentDate}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container">
        {/* Introduction */}
        <section className="terms-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Service Terms and Conditions</h2>
            <p>
              These terms and conditions govern your use of SATYA HEART CLINIC's
              services, website, and facilities. By using our services, you
              agree to these terms.
            </p>
          </motion.div>
        </section>

        {/* Acceptance of Terms */}
        <section className="terms-section">
          <motion.div
            className="terms-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="card-header">
              <FaUserCheck className="card-icon" />
              <h3>Acceptance of Terms</h3>
            </div>
            <div className="card-content">
              <p>
                By accessing and using SATYA HEART CLINIC's services, website,
                or facilities, you acknowledge that you have read, understood,
                and agree to be bound by these Terms of Service. If you do not
                agree to these terms, please do not use our services.
              </p>

              <h4>Modifications to Terms</h4>
              <p>
                We reserve the right to modify these terms at any time. Changes
                will be effective immediately upon posting on our website. Your
                continued use of our services constitutes acceptance of any
                modifications.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Medical Services */}
        <section className="terms-section">
          <motion.div
            className="terms-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="card-header">
              <FaHandshake className="card-icon" />
              <h3>Medical Services and Care</h3>
            </div>
            <div className="card-content">
              <h4>Scope of Services</h4>
              <ul>
                <li>Cardiac consultations and diagnostic services</li>
                <li>Interventional cardiology procedures</li>
                <li>Preventive cardiology and lifestyle counseling</li>
                <li>Emergency cardiac care (when available)</li>
                <li>Follow-up care and monitoring</li>
              </ul>

              <h4>Medical Disclaimer</h4>
              <p>
                While we strive to provide the highest quality medical care, no
                medical treatment can guarantee specific outcomes. Medical
                results vary based on individual conditions, and we cannot
                guarantee cure or prevention of any disease.
              </p>

              <h4>Emergency Situations</h4>
              <p>
                In case of medical emergencies, please call emergency services
                (108) immediately. Our clinic provides scheduled appointments
                and may not be equipped for all emergency situations.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Appointments and Scheduling */}
        <section className="terms-section">
          <motion.div
            className="terms-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card-header">
              <FaFileContract className="card-icon" />
              <h3>Appointments and Scheduling</h3>
            </div>
            <div className="card-content">
              <h4>Appointment Booking</h4>
              <ul>
                <li>
                  Appointments must be booked in advance through our website or
                  phone
                </li>
                <li>Walk-in appointments are subject to availability</li>
                <li>
                  We reserve the right to reschedule appointments if necessary
                </li>
                <li>
                  Patients must arrive 10 minutes before scheduled appointment
                  time
                </li>
              </ul>

              <h4>Cancellation Policy</h4>
              <ul>
                <li>24-hour notice required for appointment cancellations</li>
                <li>Late cancellations may result in a cancellation fee</li>
                <li>No-shows may affect future appointment scheduling</li>
                <li>Emergency cancellations will be handled case-by-case</li>
              </ul>

              <h4>Operating Hours</h4>
              <p>
                <strong>Regular Hours:</strong> Monday to Saturday, 6:00 PM -
                9:00 PM
                <br />
                <strong>Emergency:</strong> 24/7 on-call service for existing
                patients
              </p>
            </div>
          </motion.div>
        </section>

        {/* Patient Responsibilities */}
        <section className="terms-section">
          <motion.div
            className="terms-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="card-header">
              <FaUserCheck className="card-icon" />
              <h3>Patient Responsibilities</h3>
            </div>
            <div className="card-content">
              <h4>Patient Obligations</h4>
              <ul>
                <li>Provide accurate and complete medical history</li>
                <li>Follow prescribed treatment plans and medications</li>
                <li>Attend scheduled follow-up appointments</li>
                <li>Inform us of any changes in health status</li>
                <li>Respect clinic policies and staff</li>
                <li>Maintain appropriate behavior during visits</li>
              </ul>

              <h4>Financial Responsibilities</h4>
              <ul>
                <li>Payment is due at the time of service</li>
                <li>Insurance claims are patient's responsibility</li>
                <li>Outstanding balances may affect future appointments</li>
                <li>We accept cash, cards, and digital payments</li>
              </ul>
            </div>
          </motion.div>
        </section>

        {/* Limitations and Disclaimers */}
        <section className="terms-section">
          <motion.div
            className="terms-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="card-header">
              <FaExclamationTriangle className="card-icon" />
              <h3>Limitations and Disclaimers</h3>
            </div>
            <div className="card-content">
              <h4>Service Limitations</h4>
              <ul>
                <li>We provide cardiac care services only</li>
                <li>
                  Non-cardiac conditions may require referral to specialists
                </li>
                <li>Some procedures may require hospital admission</li>
                <li>We do not provide 24/7 emergency room services</li>
              </ul>

              <h4>Liability Limitations</h4>
              <p>
                SATYA HEART CLINIC's liability is limited to the extent
                permitted by law. We are not liable for indirect, incidental, or
                consequential damages arising from the use of our services.
              </p>

              <h4>Third-Party Services</h4>
              <p>
                We may refer patients to third-party providers for certain
                services. We are not responsible for the quality or outcomes of
                third-party services.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Privacy and Confidentiality */}
        <section className="terms-section">
          <motion.div
            className="terms-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="card-header">
              <FaGavel className="card-icon" />
              <h3>Privacy and Confidentiality</h3>
            </div>
            <div className="card-content">
              <h4>Medical Confidentiality</h4>
              <p>
                We maintain strict confidentiality of all patient information in
                accordance with medical privacy laws and our Privacy Policy.
                Your medical records are protected and will not be disclosed
                without your consent, except as required by law.
              </p>

              <h4>Data Protection</h4>
              <ul>
                <li>All patient data is encrypted and securely stored</li>
                <li>
                  Access to medical records is restricted to authorized
                  personnel
                </li>
                <li>
                  We comply with all applicable data protection regulations
                </li>
                <li>Patient consent is required for information sharing</li>
              </ul>
            </div>
          </motion.div>
        </section>

        {/* Contact and Support */}
        <section className="terms-section">
          <motion.div
            className="terms-contact"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3>Questions About These Terms?</h3>
            <p>
              If you have any questions or need clarification about these terms
              of service, please contact us:
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <strong>Clinic Director:</strong> Dr. Satya Sudhish Nimmagadda
              </div>
              <div className="contact-item">
                <strong>Email:</strong>{" "}
                <a href="mailto:satya.s.nimmagadda@gmail.com">
                  satya.s.nimmagadda@gmail.com
                </a>
              </div>
              <div className="contact-item">
                <strong>Helpdesk:</strong>{" "}
                <a href={`tel:${HELPDESK_PHONE_TEL}`}>{HELPDESK_PHONE_DISPLAY}</a>
              </div>
              <div className="contact-item">
                <strong>Emergency:</strong>{" "}
                <a href={`tel:${EMERGENCY_PHONE_TEL}`}>
                  {EMERGENCY_PHONE_DISPLAY}
                </a>
              </div>
              <div className="contact-item">
                <strong>Address:</strong>{" "}
                <a
                  href={CLINIC_GOOGLE_MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="address-link"
                  title="Click to open in Google Maps"
                >
                  Shop No-6, Shopping Complex, Vasanth Nagar, IDPL Staff
                  Cooperative Housing Society, Vasanth Nagar, Kukatpally,
                  Hyderabad, Telangana 500085
                </a>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default Terms;
